import { lazy, StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import posthog from 'posthog-js'
import { App } from './App'
import './styles/globals.css'
import 'react-international-phone/style.css'
import './i18n'
import { initDesktopShell, isDesktopShell } from '@/lib/desktopShell'
import { capturer as capturerProvenance } from '@/lib/provenance'
import { capturer as capturerCodePartenaire } from '@/lib/codePartenaire'
import { protegerDomTraduction } from '@/lib/protegerDomTraduction'
import { initGA } from '@/lib/analytics/ga'
import { isConsoleHost } from '@/console/host'
import { isPartnerHost } from '@/partenaires/host'

// console.callizy.ca : la console de l'équipe, et rien du tableau de bord
// client (ni PostHog, ni GA, ni l'app Mac). Son code n'est chargé que là.
// partenaires.callizy.ca : l'espace des partenaires publics, de la même façon.
const surPortail = isPartnerHost()
const surConsole = isConsoleHost() || surPortail
const ConsoleApp = lazy(() => import('@/console/ConsoleApp').then((m) => ({ default: m.ConsoleApp })))
const PortailApp = lazy(() => import('@/partenaires/PortailApp').then((m) => ({ default: m.PortailApp })))

// Hors WKWebView (web normal) : no-op. Sinon : bind window.callizyDesktop pour
// que les URLs callizy:// / tel: / Universal Links reçues par le shell macOS
// puissent être traitées même avant que l'utilisateur soit authentifié.
// AVANT tout rendu React : Google Translate remplace les nœuds texte sous
// React, qui tente ensuite d'insérer ou retirer des nœuds devenus orphelins.
// Sans cette protection, toute l'interface tombe sur un NotFoundError.
protegerDomTraduction()

if (!surConsole) initDesktopShell()

// Where the visitor came from before callizy.ca, read once per tab before any
// route change can lose the URL. The Mac app is ours: not a visit.
if (!surConsole && !isDesktopShell()) capturerProvenance()
// A partner's referral link that leads straight to the sign-up (lib/codePartenaire).
if (!surConsole) capturerCodePartenaire()

// Force le viewport desktop (1280px) AVANT le mount React si :
// - on est sur /dashboard ou /manager
// - et l'utilisateur a déjà cliqué "Continue with browser" (flag localStorage)
// Cela garantit que les media queries Tailwind (md:, lg:, …) résolvent dès
// le premier render comme sur PC, sinon les composants type PhoneTab restent
// figés sur leur branche mobile (cf. hidden md:flex etc.).
const isDashboardOrManager = /\/(dashboard|manager)(\/|\?|$)/.test(
  window.location.pathname + window.location.search,
)
const mobileAck = (() => {
  try {
    return localStorage.getItem('callizy_mobile_browser_ack') === '1'
  } catch {
    return false
  }
})()
if (isDashboardOrManager && mobileAck) {
  const meta = document.querySelector('meta[name="viewport"]')
  if (meta) meta.setAttribute('content', 'width=1280, user-scalable=yes')
}

const phKey = import.meta.env.VITE_POSTHOG_KEY
if (phKey && !surConsole) {
  posthog.init(phKey, {
    // Le projet est en Europe : un defaut americain enverrait les evenements
    // dans le vide, sans erreur, le jour ou la variable manque.
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    // Behind our own reverse proxy (VITE_POSTHOG_HOST), links and the toolbar
    // still have to open PostHog itself.
    ui_host: 'https://eu.posthog.com',
    capture_pageview: 'history_change',
    // Toutes les sessions sont filmees (decision du 19 oct 2026). Les champs
    // de saisie restent masques, comme par defaut dans PostHog.
  })
}

// GA4 (cross-domain avec le site marketing callizy.ca). No-op si
// VITE_GA_MEASUREMENT_ID est absent (dev local). Les page_view sont emis par
// LocaleLayout a chaque changement de route React Router.
if (!surConsole) initGA()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {surPortail ? <Suspense fallback={null}><PortailApp /></Suspense>
      : surConsole ? <Suspense fallback={null}><ConsoleApp /></Suspense> : <App />}
  </StrictMode>,
)
